import { SpaceBetween, Button, Header, Box } from '@amzn/awsui-components-core';
import {
  ContentUserRole,
  ContentUserRoleDefinition,
  ContentUserRoleFilter,
  TechDomain,
} from '@amzn/awscat-pattern-library-service-model';
import React, { useEffect, useState, type JSX } from 'react';
import { localLog } from '../../../loggers/Logger';
import ContentUserRoleGroup from './ContentUserRoleGroup';
import { ContentUserRoleMultiSelect } from './ContentUserRoleMultiSelect';
import { v4 as uuid } from 'uuid';
import constants from '../../../common/constants';

interface ContentUserRolesSectionProps {
  roles: ContentUserRoleDefinition[];
  onContentUserRolesChanged: (contentUserRoles: ContentUserRoleDefinition[]) => void;
}

export function ContentUserRolesSection(props: ContentUserRolesSectionProps) {
  const createContentUserRoleGroups = (contentUserRoles: ContentUserRoleDefinition[]) => {
    if (!contentUserRoles || contentUserRoles.length === 0) {
      return [];
    }
    const contentRolesMap = new Map<ContentUserRole, Set<TechDomain>>();
    for (const rd of contentUserRoles) {
      let technologies = contentRolesMap.get(rd.contentRole);

      if (!technologies) {
        technologies = new Set();
      }

      if (rd.filter) {
        if (['mainTechDomain', 'techDomains'].includes(rd.filter.metadataAttributeName)) {
          technologies.add(TechDomain[rd.filter.metadataAttributeValue as keyof typeof TechDomain]);
          contentRolesMap.set(rd.contentRole, technologies);
        } else {
          localLog.warn(
            `Content user role with unsupported metadata attribute name "${rd.filter.metadataAttributeName}"`,
          );
        }
      } else {
        contentRolesMap.set(rd.contentRole, technologies);
      }
    }

    const entries = Array.from(contentRolesMap.entries());

    const roleTechnologyGroups: ContentUserRoleGroup[] = [];
    while (entries.length > 0) {
      // @ts-expect-error TS2488 - strict migration
      const [curRole, curTechnologies] = entries.pop();

      const curGrp: ContentUserRoleGroup = {
        groupId: uuid(),
        roles: [curRole],
        technologies: Array.from(curTechnologies),
      };
      for (const [idx, [role, technologies]] of entries.entries()) {
        if (
          technologies.size === curTechnologies.size &&
          Array.from(technologies).every((tt) => Array.from(curTechnologies).includes(tt))
        ) {
          curGrp.roles.push(role);
          entries.splice(idx, 1);
        }
      }

      roleTechnologyGroups.push(curGrp);
    }
    return roleTechnologyGroups;
  };

  const [contentUserRoleGroups, setContentUserRoleGroups] = useState<ContentUserRoleGroup[]>(
    createContentUserRoleGroups(props.roles),
  );

  const handleContentUserRolesChanged = (grp: ContentUserRoleGroup) => {
    const idx = contentUserRoleGroups.findIndex((g) => g.groupId === grp.groupId);
    if (idx === -1) {
      throw new Error('invalid content user role group index');
    }
    contentUserRoleGroups[idx] = grp;
    setContentUserRoleGroups([...contentUserRoleGroups]);
  };

  const handleRoleDefinitionDeleted = (groupId: string) => {
    const idx = contentUserRoleGroups.findIndex((grp) => grp.groupId === groupId);
    if (idx === -1) {
      throw new Error('invalid content user role group index');
    }
    contentUserRoleGroups.splice(idx, 1);
    const newContentUserRoleGroups = [...contentUserRoleGroups];
    setContentUserRoleGroups(newContentUserRoleGroups);
  };

  const addBlankContentUserRoleDefinition = () => {
    setContentUserRoleGroups([
      ...contentUserRoleGroups,
      { groupId: uuid(), roles: [], technologies: [] },
    ]);
  };

  useEffect(() => {
    setContentUserRoleGroups(createContentUserRoleGroups(props.roles));
  }, [props.roles]);

  useEffect(() => {
    const contentUserRoles: ContentUserRoleDefinition[] = [];
    for (const grp of contentUserRoleGroups) {
      for (const role of grp.roles) {
        if (grp.technologies.length === 0) {
          contentUserRoles.push(
            new ContentUserRoleDefinition({
              contentRole: role,
            }),
          );
        } else {
          for (const technology of grp.technologies) {
            contentUserRoles.push(
              new ContentUserRoleDefinition({
                contentRole: role,
                filter: new ContentUserRoleFilter({
                  metadataAttributeName: 'mainTechDomain',
                  metadataAttributeValue: technology,
                }),
              }),
            );
            contentUserRoles.push(
              new ContentUserRoleDefinition({
                contentRole: role,
                filter: new ContentUserRoleFilter({
                  metadataAttributeName: 'techDomains',
                  metadataAttributeValue: technology,
                }),
              }),
            );
          }
        }
      }
    }

    props.onContentUserRolesChanged(contentUserRoles);
  }, [contentUserRoleGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  const contentUserRoleMultiSelects: JSX.Element[] = [];
  for (const [idx, grp] of contentUserRoleGroups.entries()) {
    contentUserRoleMultiSelects.push(
      <ContentUserRoleMultiSelect
        key={grp.groupId}
        sequenceId={idx + 1}
        roleGroup={grp}
        onSelectedRolesUpdated={handleContentUserRolesChanged}
        onRoleDefinitionDeleted={handleRoleDefinitionDeleted}
      />,
    );
  }

  const headerInfo = (
    <Box color="text-body-secondary" fontSize="body-s" display="inline">
      {constants.USER_PERMISSION_MGMT_CONTENT_USER_ROLES_INFO}
    </Box>
  );

  return (
    <>
      <Header variant="h3" info={headerInfo}>
        {constants.USER_PERMISSION_MGMT_CONTENT_USER_ROLES_HEADER}
      </Header>
      <SpaceBetween size="l">
        {contentUserRoleMultiSelects}
        <Button iconName="add-plus" onClick={addBlankContentUserRoleDefinition}>
          {constants.USER_PERMISSION_MGMT_CONTENT_USER_ROLES_ADD_BUTTON_TEXT}
        </Button>
      </SpaceBetween>
    </>
  );
}
